import React, { useCallback, useEffect, useMemo, useState } from "react";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  MenuItem,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha } from "@mui/material/styles";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { Link as RouterLink } from "react-router-dom";
import { ROLE_ADMIN_EXPORT_SETTINGS, ROLE_ADMIN_WORDING_GUARD_WRITE } from "../constants/appRoles";
import {
  creatorSourceLanguageSelectLabels,
  normalizeEventLanguage,
  supportedEventLanguages,
} from "../constants/languages";
import { useExportCodePreference } from "../context/ExportCodePreferenceContext";
import { useWordingGuardApi } from "../hooks/useWordingGuardApi";
import AdminRoleInfoTooltip from "./AdminRoleInfoTooltip";
import NeedsAttentionWordingChip from "./NeedsAttentionWordingChip";
import type { WordingScanChipState } from "./NeedsAttentionWordingChip";
import type {
  ActionTextSuggestionRow,
  WordingGuardAnalysisResponse,
  WordingGuardAnalysisScope,
  WordingReplacementRow,
} from "../shared/wordingGuard";

/** No MUI accordion chrome / box border (used for wording guard sub-accordions). */
const FLAT_ACCORDION_SX = {
  border: "none",
  boxShadow: "none",
  "&::before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

type AdminWordingGuardPanelProps = {
  active: boolean;
  initialAnalysis?: WordingGuardAnalysisResponse | null;
  onAnalysisUpdated?: (analysis: WordingGuardAnalysisResponse) => void;
  /** Mirrors the Needs Attention wording chip in the **Wording violations** accordion title. */
  needsAttentionWordingChip?: {
    scanState: WordingScanChipState;
    violationEntryCount: number;
    busy: boolean;
    onChipActivate: () => void;
  };
};

type DraftRule = {
  sourceWords: string;
  targetWord: string;
  languageCode: string;
  isActive: boolean;
};

type DraftActionSuggestion = {
  text: string;
  languageCode: string;
  isActive: boolean;
  sortOrder: number;
};

const emptyDraft: DraftRule = {
  sourceWords: "",
  targetWord: "",
  languageCode: "ger",
  isActive: true,
};

const emptyActionSuggestionDraft: DraftActionSuggestion = {
  text: "",
  languageCode: "ger",
  isActive: true,
  sortOrder: 0,
};

const parseSourceWords = (input: string): string[] => {
  const seen = new Set<string>();
  const words: string[] = [];
  for (const rawPart of input.split(",")) {
    const trimmed = rawPart.trim();
    if (!trimmed) continue;
    const key = trimmed.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    words.push(trimmed);
  }
  return words;
};

const formatApiError = (err: { message?: string; responseBody?: string }): string => {
  const raw = err.responseBody;
  if (!raw) return err.message || "Request failed.";
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const lines: string[] = [];
    for (const [key, value] of Object.entries(parsed)) {
      if (Array.isArray(value)) {
        const flat = value.map((v) => String(v)).join(" ");
        lines.push(key === "non_field_errors" ? flat : `${key}: ${flat}`);
      } else if (value && typeof value === "object") {
        lines.push(`${key}: ${JSON.stringify(value)}`);
      } else if (value != null) {
        lines.push(key === "detail" ? String(value) : `${key}: ${String(value)}`);
      }
    }
    return lines.join(" ");
  } catch {
    return raw || err.message || "Request failed.";
  }
};

export default function AdminWordingGuardPanel({
  active,
  initialAnalysis = null,
  onAnalysisUpdated,
  needsAttentionWordingChip,
}: AdminWordingGuardPanelProps) {
  const {
    listWordingRules,
    createWordingRule,
    updateWordingRule,
    deleteWordingRule,
    analyzeWordingGuard,
    applyWordingToDescription,
    listActionSuggestions,
    createActionSuggestion,
    updateActionSuggestion,
    deleteActionSuggestion,
  } = useWordingGuardApi();
  const {
    wordingGuardEnabled,
    setWordingGuardEnabled,
    textSuggestionsEnabled,
    setTextSuggestionsEnabled,
    settingsLoaded,
  } = useExportCodePreference();
  const [rows, setRows] = useState<WordingReplacementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisScope, setAnalysisScope] = useState<WordingGuardAnalysisScope>("latest");
  const [analysis, setAnalysis] = useState<WordingGuardAnalysisResponse | null>(null);
  const [applyingByDescription, setApplyingByDescription] = useState<Record<number, boolean>>({});
  const [rulesSectionOpen, setRulesSectionOpen] = useState(false);
  const [analysisSectionOpen, setAnalysisSectionOpen] = useState(false);
  const [textSuggestionsSectionOpen, setTextSuggestionsSectionOpen] = useState(false);
  /** Per-language page index for Wording violations (analysis) entry lists. */
  const [analysisEntryPageByLang, setAnalysisEntryPageByLang] = useState<Record<string, number>>({});
  const [analysisEntriesRowsPerPage, setAnalysisEntriesRowsPerPage] = useState(10);
  const [actionSuggestionsPage, setActionSuggestionsPage] = useState(0);
  const [actionSuggestionsRowsPerPage, setActionSuggestionsRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [draftById, setDraftById] = useState<Record<number, DraftRule>>({});
  const [newDraft, setNewDraft] = useState<DraftRule>(emptyDraft);
  const [actionSuggestions, setActionSuggestions] = useState<ActionTextSuggestionRow[]>([]);
  const [actionSuggestionDraftById, setActionSuggestionDraftById] = useState<Record<number, DraftActionSuggestion>>({});
  const [newActionSuggestionDraft, setNewActionSuggestionDraft] = useState<DraftActionSuggestion>(emptyActionSuggestionDraft);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, suggestionRows] = await Promise.all([
        listWordingRules(),
        listActionSuggestions({ active: "all" }),
      ]);
      setRows(data);
      setActionSuggestions(suggestionRows);
      const draftMap: Record<number, DraftRule> = {};
      for (const row of data) {
        draftMap[row.id] = {
          sourceWords: row.source_words.join(", "),
          targetWord: row.target_word,
          languageCode: row.language_code,
          isActive: row.is_active,
        };
      }
      setDraftById(draftMap);
      const suggestionDraftMap: Record<number, DraftActionSuggestion> = {};
      for (const row of suggestionRows) {
        suggestionDraftMap[row.id] = {
          text: row.text,
          languageCode: row.language_code,
          isActive: row.is_active,
          sortOrder: row.sort_order ?? 0,
        };
      }
      setActionSuggestionDraftById(suggestionDraftMap);
      setActionSuggestionsPage(0);
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string };
      setError(formatApiError(err) || "Could not load wording guard rules.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [listActionSuggestions, listWordingRules]);

  useEffect(() => {
    if (active) {
      void load();
    }
  }, [active, load]);

  useEffect(() => {
    if (!initialAnalysis) return;
    setAnalysis(initialAnalysis);
    setAnalysisScope(initialAnalysis.scope);
    setAnalysisEntryPageByLang({});
  }, [initialAnalysis]);

  useEffect(() => {
    setAnalysisEntryPageByLang({});
  }, [analysis?.scope, analysis?.total_entries]);

  const rowsSorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const la = (a.language_code || "").toLowerCase();
        const lb = (b.language_code || "").toLowerCase();
        if (la !== lb) return la.localeCompare(lb);
        return a.target_word.localeCompare(b.target_word);
      }),
    [rows]
  );

  const rowsByLanguage = useMemo(() => {
    const groups: Record<string, WordingReplacementRow[]> = {};
    for (const row of rowsSorted) {
      const lang = (row.language_code || "ger").toLowerCase();
      if (!groups[lang]) groups[lang] = [];
      groups[lang].push(row);
    }
    return groups;
  }, [rowsSorted]);

  const languagesSorted = useMemo(() => Object.keys(rowsByLanguage).sort(), [rowsByLanguage]);
  const activeCount = useMemo(() => rows.filter((row) => row.is_active).length, [rows]);
  const activeActionSuggestionCount = useMemo(
    () => actionSuggestions.filter((row) => row.is_active).length,
    [actionSuggestions]
  );
  const actionSuggestionsSorted = useMemo(
    () =>
      [...actionSuggestions].sort((a, b) => {
        const la = (a.language_code || "").toLowerCase();
        const lb = (b.language_code || "").toLowerCase();
        if (la !== lb) return la.localeCompare(lb);
        const orderDiff = Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return a.text.localeCompare(b.text);
      }),
    [actionSuggestions]
  );
  const pagedActionSuggestions = useMemo(
    () =>
      actionSuggestionsSorted.slice(
        actionSuggestionsPage * actionSuggestionsRowsPerPage,
        actionSuggestionsPage * actionSuggestionsRowsPerPage + actionSuggestionsRowsPerPage
      ),
    [actionSuggestionsSorted, actionSuggestionsPage, actionSuggestionsRowsPerPage]
  );

  const handleActionSuggestionDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;
      if (sourceIndex === destinationIndex) return;

      const pageStart = actionSuggestionsPage * actionSuggestionsRowsPerPage;
      const absoluteSource = pageStart + sourceIndex;
      const absoluteDestination = pageStart + destinationIndex;
      const reordered = [...actionSuggestionsSorted];
      const [moved] = reordered.splice(absoluteSource, 1);
      reordered.splice(absoluteDestination, 0, moved);

      const sortOrderById = new Map<number, number>();
      reordered.forEach((row, index) => {
        sortOrderById.set(row.id, index + 1);
      });
      const changedRows = reordered.filter((row) => {
        const nextOrder = sortOrderById.get(row.id) ?? row.sort_order;
        return Number(row.sort_order ?? 0) !== Number(nextOrder ?? 0);
      });

      setActionSuggestions((prev) =>
        prev.map((row) => ({
          ...row,
          sort_order: sortOrderById.get(row.id) ?? row.sort_order,
        }))
      );
      setActionSuggestionDraftById((prev) => {
        const next = { ...prev };
        for (const [id, sortOrder] of sortOrderById.entries()) {
          if (!next[id]) continue;
          next[id] = { ...next[id], sortOrder };
        }
        return next;
      });

      void (async () => {
        if (!changedRows.length) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
          await Promise.all(
            changedRows.map((row) =>
              updateActionSuggestion(row.id, {
                text: row.text,
                language_code: normalizeEventLanguage(row.language_code) || "ger",
                is_active: row.is_active,
                sort_order: sortOrderById.get(row.id) ?? Number(row.sort_order ?? 0),
              })
            )
          );
          setSuccess(`Saved new order for ${changedRows.length} action suggestions.`);
          await load();
        } catch (e: unknown) {
          const err = e as { message?: string; responseBody?: string; status?: number };
          const hint =
            err.status === 403
              ? " Permission denied — assign the required API app role in Entra, then sign out and in."
              : "";
          setError((formatApiError(err) || "Saving reordered action suggestions failed.") + hint);
          await load();
        } finally {
          setSaving(false);
        }
      })();
    },
    [
      actionSuggestionsPage,
      actionSuggestionsRowsPerPage,
      actionSuggestionsSorted,
      load,
      updateActionSuggestion,
    ]
  );

  const saveExisting = async (id: number) => {
    const draft = draftById[id];
    if (!draft) return;
    const sourceWords = parseSourceWords(draft.sourceWords);
    if (sourceWords.length === 0 || !draft.targetWord.trim()) {
      setError("Each rule needs at least one source word and one target word.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateWordingRule(id, {
        source_words: sourceWords,
        target_word: draft.targetWord.trim(),
        language_code: normalizeEventLanguage(draft.languageCode) || "ger",
        is_active: draft.isActive,
      });
      setSuccess(`Rule ${id} saved.`);
      await load();
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string; status?: number };
      const hint =
        err.status === 403
          ? " Permission denied — assign the required API app role in Entra, then sign out and in."
          : "";
      setError((formatApiError(err) || "Saving failed.") + hint);
    } finally {
      setSaving(false);
    }
  };

  const createNew = async () => {
    const sourceWords = parseSourceWords(newDraft.sourceWords);
    if (sourceWords.length === 0 || !newDraft.targetWord.trim()) {
      setError("New rule needs at least one source word and one target word.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await createWordingRule({
        source_words: sourceWords,
        target_word: newDraft.targetWord.trim(),
        language_code: normalizeEventLanguage(newDraft.languageCode) || "ger",
        is_active: newDraft.isActive,
      });
      setNewDraft(emptyDraft);
      setSuccess("Wording rule created.");
      await load();
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string; status?: number };
      const hint =
        err.status === 403
          ? " Permission denied — assign the required API app role in Entra, then sign out and in."
          : "";
      setError((formatApiError(err) || "Create failed.") + hint);
    } finally {
      setSaving(false);
    }
  };

  const removeRule = async (id: number) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteWordingRule(id);
      setSuccess(`Rule ${id} deleted.`);
      await load();
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string; status?: number };
      const hint =
        err.status === 403
          ? " Permission denied — assign the required API app role in Entra, then sign out and in."
          : "";
      setError((formatApiError(err) || "Delete failed.") + hint);
    } finally {
      setSaving(false);
    }
  };

  const saveExistingActionSuggestion = async (id: number) => {
    const draft = actionSuggestionDraftById[id];
    if (!draft || !draft.text.trim()) {
      setError("Each action suggestion needs text.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateActionSuggestion(id, {
        text: draft.text.trim(),
        language_code: normalizeEventLanguage(draft.languageCode) || "ger",
        is_active: draft.isActive,
        sort_order: Number(draft.sortOrder) || 0,
      });
      setSuccess(`Action suggestion ${id} saved.`);
      await load();
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string; status?: number };
      const hint =
        err.status === 403
          ? " Permission denied — assign the required API app role in Entra, then sign out and in."
          : "";
      setError((formatApiError(err) || "Saving action suggestion failed.") + hint);
    } finally {
      setSaving(false);
    }
  };

  const createNewActionSuggestion = async () => {
    if (!newActionSuggestionDraft.text.trim()) {
      setError("Action suggestion text is required.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await createActionSuggestion({
        text: newActionSuggestionDraft.text.trim(),
        language_code: normalizeEventLanguage(newActionSuggestionDraft.languageCode) || "ger",
        is_active: newActionSuggestionDraft.isActive,
        sort_order:
          Math.max(
            0,
            ...actionSuggestions.map((row) => Number(row.sort_order ?? 0))
          ) + 1,
      });
      setNewActionSuggestionDraft(emptyActionSuggestionDraft);
      setSuccess("Action suggestion created.");
      await load();
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string; status?: number };
      const hint =
        err.status === 403
          ? " Permission denied — assign the required API app role in Entra, then sign out and in."
          : "";
      setError((formatApiError(err) || "Creating action suggestion failed.") + hint);
    } finally {
      setSaving(false);
    }
  };

  const removeActionSuggestion = async (id: number) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteActionSuggestion(id);
      setSuccess(`Action suggestion ${id} deleted.`);
      await load();
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string; status?: number };
      const hint =
        err.status === 403
          ? " Permission denied — assign the required API app role in Entra, then sign out and in."
          : "";
      setError((formatApiError(err) || "Deleting action suggestion failed.") + hint);
    } finally {
      setSaving(false);
    }
  };

  const runAnalysis = async (scope: WordingGuardAnalysisScope) => {
    setAnalysisScope(scope);
    setAnalysisLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await analyzeWordingGuard(scope);
      setAnalysis(result);
      setAnalysisEntryPageByLang({});
      onAnalysisUpdated?.(result);
      setSuccess(
        `Analysis finished: ${result.total_entries} entries with ${result.total_violations} total hits.`
      );
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string };
      setError(formatApiError(err) || "Analysis failed.");
      setAnalysis(null);
      setAnalysisEntryPageByLang({});
    } finally {
      setAnalysisLoading(false);
    }
  };

  const applyForDescription = async (descriptionId: number) => {
    setApplyingByDescription((prev) => ({ ...prev, [descriptionId]: true }));
    setError(null);
    setSuccess(null);
    try {
      const response = await applyWordingToDescription(descriptionId);
      let removedEntry = false;
      let removedEventCode: number | null = null;
      setAnalysis((prev) => {
        if (!prev) return prev;
        const nextLanguages = prev.languages
          .map((group) => {
            const remainingEntries = group.entries.filter((entry) => entry.description_id !== descriptionId);
            const didRemoveInGroup = remainingEntries.length !== group.entries.length;
            if (didRemoveInGroup && removedEventCode == null) {
              const removed = group.entries.find((entry) => entry.description_id === descriptionId);
              removedEventCode = removed?.event_code ?? null;
            }
            removedEntry = removedEntry || didRemoveInGroup;
            const remainingHits = remainingEntries.reduce((sum, entry) => sum + entry.hits.length, 0);
            return {
              ...group,
              entries: remainingEntries,
              entry_count: remainingEntries.length,
              total_hits: remainingHits,
            };
          })
          .filter((group) => group.entries.length > 0);
        if (!removedEntry) return prev;
        const nextTotalEntries = nextLanguages.reduce((sum, group) => sum + group.entry_count, 0);
        const nextTotalViolations = nextLanguages.reduce((sum, group) => sum + group.total_hits, 0);
        const nextAnalysis = {
          ...prev,
          languages: nextLanguages,
          total_entries: nextTotalEntries,
          total_violations: nextTotalViolations,
        };
        onAnalysisUpdated?.(nextAnalysis);
        return nextAnalysis;
      });
      setSuccess(
        removedEntry && removedEventCode != null
          ? `Applied ${response.replacements ?? 0} replacements for event ${removedEventCode}. Removed from current analysis view.`
          : `Applied ${response.replacements ?? 0} replacements for description ${descriptionId}.`
      );
    } catch (e: unknown) {
      const err = e as { message?: string; responseBody?: string };
      setError(formatApiError(err) || "Apply failed.");
    } finally {
      setApplyingByDescription((prev) => ({ ...prev, [descriptionId]: false }));
    }
  };

  return (
    <Box sx={{ px: 1.5, py: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <Stack direction="column" spacing={1} sx={{ p: 1.25, borderRadius: 1, backgroundColor: "action.hover" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", color: "text.secondary" }}>
          Global settings
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Wording guard globally active</Typography>
          <Tooltip
            title="Global runtime switch: when enabled, wording guard suggestions/checks are active for event text processing; when disabled, all wording guard rules are ignored."
            arrow
          >
            <InfoOutlinedIcon
              fontSize="small"
              color="action"
              sx={{ cursor: "help", verticalAlign: "middle" }}
              aria-label="What does wording guard globally active mean?"
            />
          </Tooltip>
          <Switch
            size="small"
            checked={wordingGuardEnabled}
            disabled={!settingsLoaded || saving}
            onChange={async (_, checked) => {
              try {
                setError(null);
                setSuccess(null);
                await setWordingGuardEnabled(checked);
                setSuccess(`Wording guard ${checked ? "enabled" : "disabled"} globally.`);
              } catch (e: unknown) {
                const err = e as { message?: string };
                setError(err.message || "Could not update wording guard runtime switch.");
              }
            }}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Text suggestions globally active</Typography>
          <Tooltip
            title="Global runtime switch: when enabled, text suggestions are shown in creator inputs; when disabled, suggestions are hidden."
            arrow
          >
            <InfoOutlinedIcon
              fontSize="small"
              color="action"
              sx={{ cursor: "help", verticalAlign: "middle" }}
              aria-label="What does text suggestions globally active mean?"
            />
          </Tooltip>
          <Switch
            size="small"
            checked={textSuggestionsEnabled}
            disabled={!settingsLoaded || saving}
            onChange={async (_, checked) => {
              try {
                setError(null);
                setSuccess(null);
                await setTextSuggestionsEnabled(checked);
                setSuccess(`Text suggestions ${checked ? "enabled" : "disabled"} globally.`);
              } catch (e: unknown) {
                const err = e as { message?: string };
                setError(err.message || "Could not update text suggestions runtime switch.");
              }
            }}
          />
        </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ alignSelf: { sm: "center" } }}>
          <Typography variant="caption" color="text.secondary" component="span">
            API write access
          </Typography>
          <AdminRoleInfoTooltip ariaLabel="Wording guard and global settings API write permissions in Entra">
            <>
              Creating, editing, or deleting wording rules and action text suggestions, running analysis, and applying
              replacements require the <strong>API application role</strong>{" "}
              <code>{ROLE_ADMIN_WORDING_GUARD_WRITE}</code> in Microsoft Entra on the API app. Changing global
              switches in this section requires <strong>API application role</strong>{" "}
              <code>{ROLE_ADMIN_EXPORT_SETTINGS}</code> (or the role configured on the server via environment
              variables).
            </>
          </AdminRoleInfoTooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Accordion
        disableGutters
        elevation={0}
        expanded={rulesSectionOpen}
        onChange={(_, expanded) => setRulesSectionOpen(expanded)}
        sx={{ ...FLAT_ACCORDION_SX, borderRadius: 1, mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <RuleFolderIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Rule Configuration
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        <PlaylistAddCheckIcon color="primary" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Configure wording replacements for multi-language text input. One rule can map multiple old terms to one target term.
        </Typography>
        <Chip size="small" label={`${rows.length} rules`} />
        <Chip size="small" color="success" label={`${activeCount} active`} />
      </Stack>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={() => void load()}
          disabled={loading || saving}
        >
          Refresh list
        </Button>
      </Stack>
      <TableContainer>
      <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell>Source words (comma-separated)</TableCell>
            <TableCell>Target word</TableCell>
            <TableCell>Language</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <TextField
                size="small"
                fullWidth
                placeholder="Beladung, Ladung"
                value={newDraft.sourceWords}
                onChange={(e) => setNewDraft((prev) => ({ ...prev, sourceWords: e.target.value }))}
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                fullWidth
                placeholder="Last"
                value={newDraft.targetWord}
                onChange={(e) => setNewDraft((prev) => ({ ...prev, targetWord: e.target.value }))}
              />
            </TableCell>
            <TableCell>
              <TextField
                select
                size="small"
                sx={{ width: 190 }}
                value={normalizeEventLanguage(newDraft.languageCode) || "ger"}
                onChange={(e) => setNewDraft((prev) => ({ ...prev, languageCode: e.target.value }))}
              >
                {supportedEventLanguages.map((code) => (
                  <MenuItem key={code} value={code}>
                    {creatorSourceLanguageSelectLabels[code]}
                  </MenuItem>
                ))}
              </TextField>
            </TableCell>
            <TableCell>
              <Switch
                size="small"
                checked={newDraft.isActive}
                onChange={(_, v) => setNewDraft((prev) => ({ ...prev, isActive: v }))}
              />
            </TableCell>
            <TableCell>
              <Button size="small" variant="contained" onClick={() => void createNew()} disabled={saving}>
                Add rule
              </Button>
            </TableCell>
          </TableRow>

          {languagesSorted.map((lang) => (
            <React.Fragment key={lang}>
              <TableRow>
                <TableCell colSpan={5} sx={{ backgroundColor: "action.hover", fontWeight: 700 }}>
                  Language: {lang.toUpperCase()} ({rowsByLanguage[lang].length} rules)
                </TableCell>
              </TableRow>
              {rowsByLanguage[lang].map((row) => {
                const draft = draftById[row.id];
                if (!draft) return null;
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={draft.sourceWords}
                        onChange={(e) =>
                          setDraftById((prev) => ({
                            ...prev,
                            [row.id]: { ...prev[row.id], sourceWords: e.target.value },
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={draft.targetWord}
                        onChange={(e) =>
                          setDraftById((prev) => ({
                            ...prev,
                            [row.id]: { ...prev[row.id], targetWord: e.target.value },
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        sx={{ width: 190 }}
                        value={normalizeEventLanguage(draft.languageCode) || "ger"}
                        onChange={(e) =>
                          setDraftById((prev) => ({
                            ...prev,
                            [row.id]: { ...prev[row.id], languageCode: e.target.value },
                          }))
                        }
                      >
                        {supportedEventLanguages.map((code) => (
                          <MenuItem key={code} value={code}>
                            {creatorSourceLanguageSelectLabels[code]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={draft.isActive}
                        onChange={(_, v) =>
                          setDraftById((prev) => ({
                            ...prev,
                            [row.id]: { ...prev[row.id], isActive: v },
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => void saveExisting(row.id)}
                          disabled={saving}
                        >
                          Save
                        </Button>
                        <Tooltip title="Delete rule">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => void removeRule(row.id)}
                              disabled={saving}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        elevation={0}
        expanded={analysisSectionOpen}
        onChange={(_, expanded) => setAnalysisSectionOpen(expanded)}
        sx={{ ...FLAT_ACCORDION_SX, borderRadius: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: "100%", pr: 0.5 }}
          >
            <AnalyticsIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Wording violations
            </Typography>
            {needsAttentionWordingChip && (
              <Box
                sx={{ ml: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <NeedsAttentionWordingChip
                  scanState={needsAttentionWordingChip.scanState}
                  violationEntryCount={needsAttentionWordingChip.violationEntryCount}
                  busy={needsAttentionWordingChip.busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    needsAttentionWordingChip.onChipActivate();
                  }}
                />
              </Box>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Analyze existing events for wording violations and apply replacements one event at a time.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
          <Button
            size="small"
            variant={analysisScope === "latest" ? "contained" : "outlined"}
            onClick={() => void runAnalysis("latest")}
            disabled={analysisLoading || saving}
          >
            Analyze latest
          </Button>
          <Button
            size="small"
            variant={analysisScope === "approved" ? "contained" : "outlined"}
            onClick={() => void runAnalysis("approved")}
            disabled={analysisLoading || saving}
          >
            Analyze approved
          </Button>
          <Button
            size="small"
            variant={analysisScope === "unapproved" ? "contained" : "outlined"}
            onClick={() => void runAnalysis("unapproved")}
            disabled={analysisLoading || saving}
          >
            Analyze unapproved
          </Button>
        </Stack>
        {analysisLoading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Running wording analysis...</Typography>
          </Stack>
        )}
        {analysis && (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
              <Chip label={`Scope: ${analysis.scope}`} size="small" />
              <Chip label={`Entries: ${analysis.total_entries}`} size="small" />
              <Chip label={`Total hits: ${analysis.total_violations}`} size="small" color="warning" />
            </Stack>
            {analysis.languages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No violations found for this scope.
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {analysis.languages.map((group) => {
                  const langKey = (group.language_code || "ger").toLowerCase();
                  const page = analysisEntryPageByLang[langKey] ?? 0;
                  const perPage = analysisEntriesRowsPerPage;
                  const pagedEntries = group.entries.slice(
                    page * perPage,
                    page * perPage + perPage
                  );
                  return (
                  <Box key={group.language_code} sx={{ py: 0.25 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 0.25 }}>
                      {group.language_code.toUpperCase()} ({group.entry_count} entries)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Paginated per language; use page size below.
                    </Typography>
                    <Stack spacing={0}>
                      {pagedEntries.map((entry, entryIdx) => (
                        <Box
                          key={`${group.language_code}-${entry.description_id}`}
                          sx={{
                            py: 1.15,
                            pr: 0.5,
                            borderBottom: (t) =>
                              entryIdx < pagedEntries.length - 1
                                ? `1px solid ${alpha(t.palette.divider, 0.35)}`
                                : "none",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            sx={{ mb: 0.75 }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              #{entry.event_code} v{entry.version}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="text"
                                component={RouterLink}
                                to={`/admin?open_event_id=${entry.event_id}`}
                                endIcon={<OpenInNewIcon fontSize="small" />}
                              >
                                Open event details
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                disabled={Boolean(applyingByDescription[entry.description_id]) || saving}
                                onClick={() => void applyForDescription(entry.description_id)}
                              >
                                {applyingByDescription[entry.description_id]
                                  ? "Applying..."
                                  : "Apply replacements to this event"}
                              </Button>
                            </Stack>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Event ID: {entry.event_id} | {entry.approved ? "approved" : "unapproved"}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
                            {entry.hits.map((hit, idx) => (
                              <Chip
                                key={`${entry.description_id}-${idx}`}
                                size="small"
                                color="warning"
                                variant="filled"
                                sx={{ fontWeight: 500, "& .MuiChip-label": { px: 1 } }}
                                label={`${hit.field}${hit.action_index != null ? `[${hit.action_index}]` : ""}: ${hit.source} -> ${hit.target} (${hit.matches})`}
                              />
                            ))}
                          </Stack>
                          {entry.preview_changes?.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Preview before apply
                              </Typography>
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {entry.preview_changes.map((change, i) => (
                                  <Box
                                    key={`${entry.description_id}-preview-${i}`}
                                    sx={{
                                      py: 0.5,
                                      px: 0.75,
                                      borderRadius: 1,
                                      bgcolor: (t) =>
                                        t.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.06)"
                                          : "rgba(0,0,0,0.04)",
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {change.field}
                                      {change.action_index != null ? `[${change.action_index}]` : ""} ({change.replacements})
                                    </Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Before: {change.before}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="success.main">
                                      After: {change.after}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Stack>
                    <TablePagination
                      component="div"
                      count={group.entries.length}
                      page={page}
                      onPageChange={(_, p) =>
                        setAnalysisEntryPageByLang((prev) => ({ ...prev, [langKey]: p }))
                      }
                      rowsPerPage={perPage}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      onRowsPerPageChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        setAnalysisEntriesRowsPerPage(next);
                        setAnalysisEntryPageByLang({});
                      }}
                      sx={{ borderTop: "none", mt: 0.5 }}
                    />
                  </Box>
                );
                })}
              </Stack>
            )}
          </Box>
        )}
      </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion
        disableGutters
        elevation={0}
        expanded={textSuggestionsSectionOpen}
        onChange={(_, expanded) => setTextSuggestionsSectionOpen(expanded)}
        sx={{ ...FLAT_ACCORDION_SX, borderRadius: 1, mt: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PlaylistAddCheckIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Text Suggestions
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Suggestions shown in Creator action fields to keep wording consistent.
            </Typography>
            <Chip size="small" label={`${actionSuggestions.length} suggestions`} />
            <Chip size="small" color="success" label={`${activeActionSuggestionCount} active`} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Action text suggestions
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={() => void load()}
              disabled={loading || saving}
            >
              Refresh list
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56 }}>Drag</TableCell>
                  <TableCell>Action suggestion text</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={handleActionSuggestionDragEnd}>
                <Droppable droppableId="action-suggestion-rows">
                  {(provided) => (
                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                      <TableRow>
                        <TableCell />
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Situation am Fahrzeug prüfen"
                            value={newActionSuggestionDraft.text}
                            onChange={(e) =>
                              setNewActionSuggestionDraft((prev) => ({ ...prev, text: e.target.value }))
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            sx={{ width: 190 }}
                            value={normalizeEventLanguage(newActionSuggestionDraft.languageCode) || "ger"}
                            onChange={(e) =>
                              setNewActionSuggestionDraft((prev) => ({ ...prev, languageCode: e.target.value }))
                            }
                          >
                            {supportedEventLanguages.map((code) => (
                              <MenuItem key={code} value={code}>
                                {creatorSourceLanguageSelectLabels[code]}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            Auto
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={newActionSuggestionDraft.isActive}
                            onChange={(_, v) => setNewActionSuggestionDraft((prev) => ({ ...prev, isActive: v }))}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => void createNewActionSuggestion()}
                            disabled={saving}
                          >
                            Add suggestion
                          </Button>
                        </TableCell>
                      </TableRow>
                      {pagedActionSuggestions.map((row, index) => {
                        const draft = actionSuggestionDraftById[row.id];
                        if (!draft) return null;
                        const lang = (row.language_code || "ger").toLowerCase();
                        const previousLang =
                          index > 0 ? (pagedActionSuggestions[index - 1].language_code || "ger").toLowerCase() : null;
                        const showLanguageHeader = index === 0 || previousLang !== lang;
                        return (
                          <React.Fragment key={row.id}>
                            {showLanguageHeader && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ backgroundColor: "action.hover", fontWeight: 700 }}>
                                  Language: {lang.toUpperCase()}
                                </TableCell>
                              </TableRow>
                            )}
                            <Draggable draggableId={`action-suggestion-${row.id}`} index={index}>
                              {(draggableProvided, snapshot) => (
                                <TableRow
                                  hover
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  sx={{
                                    backgroundColor: snapshot.isDragging ? "action.hover" : undefined,
                                  }}
                                >
                                  <TableCell>
                                    <Tooltip title="Drag to reorder" arrow>
                                      <IconButton
                                        size="small"
                                        {...draggableProvided.dragHandleProps}
                                        sx={{ cursor: snapshot.isDragging ? "grabbing" : "grab" }}
                                      >
                                        <DragIndicatorIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={draft.text}
                                      onChange={(e) =>
                                        setActionSuggestionDraftById((prev) => ({
                                          ...prev,
                                          [row.id]: { ...prev[row.id], text: e.target.value },
                                        }))
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      select
                                      size="small"
                                      sx={{ width: 190 }}
                                      value={normalizeEventLanguage(draft.languageCode) || "ger"}
                                      onChange={(e) =>
                                        setActionSuggestionDraftById((prev) => ({
                                          ...prev,
                                          [row.id]: { ...prev[row.id], languageCode: e.target.value },
                                        }))
                                      }
                                    >
                                      {supportedEventLanguages.map((code) => (
                                        <MenuItem key={code} value={code}>
                                          {creatorSourceLanguageSelectLabels[code]}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                      {draft.sortOrder}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Switch
                                      size="small"
                                      checked={draft.isActive}
                                      onChange={(_, v) =>
                                        setActionSuggestionDraftById((prev) => ({
                                          ...prev,
                                          [row.id]: { ...prev[row.id], isActive: v },
                                        }))
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => void saveExistingActionSuggestion(row.id)}
                                        disabled={saving}
                                      >
                                        Save
                                      </Button>
                                      <Tooltip title="Delete action suggestion">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => void removeActionSuggestion(row.id)}
                                            disabled={saving}
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          </React.Fragment>
                        );
                      })}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={actionSuggestionsSorted.length}
            page={actionSuggestionsPage}
            onPageChange={(_, p) => setActionSuggestionsPage(p)}
            rowsPerPage={actionSuggestionsRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setActionSuggestionsRowsPerPage(next);
              setActionSuggestionsPage(0);
            }}
            sx={{ borderTop: "none", mt: 0.5 }}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
